/* Courted · every read and write. The household's rows (date_swipes, couples.date_range_minutes, plans) through
   the shared Supabase client and RLS; the deck and each place's card through the courted-places edge function
   (Google, server-side); our own words for a place from the catalogue DB (date_areas and date_places, public read).
   Google's terms: only place ids are stored; a card (name, address, photo) lives in memory for this session. */
import { supabase, currentSession } from '../core/supabase.js';
import { API_URL, PUBLISHABLE_KEY } from '../core/config.js';
import { CATALOGUE_URL, CATALOGUE_KEY } from '../considered/catalogue.js';
import { townName, areasNear } from './helpers.js';
import { countryOfHome, regionFromTown } from '../week/holidays.js';

const db = () => supabase();
const must = (r) => { if (r && r.error) throw r.error; return r ? r.data : null; };
let ctxP = null;

/* You, your partner, and home. → { status: 'ready' | 'signedout' | 'nocouple' | 'offline' | 'error', … } */
export function context(force) {
  if (ctxP && !force) return ctxP;
  ctxP = (async () => {
    const s = await currentSession();
    if (!s) return { status: 'signedout' };
    const uid = s.user.id;
    const mem = must(await db().from('couple_members').select('couple_id').eq('user_id', uid).maybeSingle());
    if (!mem) return { status: 'nocouple', me: { id: uid } };
    const [people, couple] = await Promise.all([
      db().from('profiles').select('id, first_name').then(must),
      db().from('couples').select('id, country_code, home_town, home_region, home_lat, home_lng, date_range_minutes').eq('id', mem.couple_id).maybeSingle().then(must),
    ]);
    let me = null, partner = null;
    (people || []).forEach((p) => { if (p.id === uid) me = p; else partner = p; });
    const c = couple || {};
    const country = countryOfHome(c.home_region, c.home_town, c.country_code) || 'CA';
    return {
      status: 'ready', coupleId: mem.couple_id,
      me: { id: uid, name: (me && me.first_name) || 'You' },
      partner: partner ? { id: partner.id, name: partner.first_name || 'Your partner' } : null,
      country, town: townName(c.home_town) || null, region: c.home_region || regionFromTown(c.home_town, country) || null,
      home: c.home_lat != null && c.home_lng != null ? { lat: Number(c.home_lat), lng: Number(c.home_lng) } : null,
      range: Number(c.date_range_minutes) || 30,
    };
  })().catch((e) => {
    ctxP = null;
    return { status: typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'error', error: String(e && e.message || e) };
  });
  return ctxP;
}

/* Everyone's answers in the home (partners read each other's, as in Curated), with the town each was made in
   (none: near home). */
const SWIPE = 'place_id, user_id, kind, verdict, town_id, created_at, updated_at';
export async function swipes(ctx) {
  return must(await db().from('date_swipes').select(SWIPE).eq('couple_id', ctx.coupleId)) || [];
}
/* Your answer. Made in a town (Go out anywhere), it's that town's; near home leaves the town as it was. */
export async function answer(ctx, placeId, kind, verdict, townId) {
  const row = { couple_id: ctx.coupleId, user_id: ctx.me.id, place_id: placeId, kind: kind || null, verdict };
  if (townId) row.town_id = townId;
  return must(await db().from('date_swipes').upsert(row, { onConflict: 'user_id,place_id' }).select(SWIPE).single());
}

/* ---- the towns you look at besides home (date_towns, the household's: 0072) ---- */
const TOWN = 'id, google_id, name, detail, country, lat, lng, km, used_at';
export async function towns(ctx) {
  return must(await db().from('date_towns').select(TOWN).eq('couple_id', ctx.coupleId).order('used_at', { ascending: false }).limit(12)) || [];
}
/* A town from the maps function's search (its id, name and what tells it apart): where it is and how far it
   reaches, kept for the household. → the date_towns row */
export async function addTown(ctx, pick, session) {
  const p = await maps({ op: 'place', id: pick.id, session });
  const t = p.place || {};
  if (t.lat == null || t.lng == null) throw new Error('no_place');
  const row = { couple_id: ctx.coupleId, google_id: pick.id, name: String(t.name || pick.name || '').slice(0, 80) || 'Somewhere',
    detail: String(pick.detail || '').slice(0, 120) || null, country: /^[A-Z]{2}$/.test(String(t.country || '')) ? t.country : null,
    lat: t.lat, lng: t.lng, km: Math.min(40, Math.max(3, Number(t.km) || 12)), used_at: new Date().toISOString() };
  return must(await db().from('date_towns').upsert(row, { onConflict: 'couple_id,google_id' }).select(TOWN).single());
}
export async function useTown(id) {
  must(await db().from('date_towns').update({ used_at: new Date().toISOString() }).eq('id', id));
}
async function maps(body) {
  const s = await currentSession();
  if (!s) throw new Error('signin');
  const r = await fetch(API_URL + '/functions/v1/maps', {
    method: 'POST', headers: { 'content-type': 'application/json', authorization: 'Bearer ' + s.access_token, apikey: PUBLISHABLE_KEY },
    body: JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({ ok: false, error: 'bad_reply' }));
  if (!j.ok) { const e = new Error(j.error || 'maps'); e.status = r.status; throw e; }
  return j;
}
/* Towns for "Somewhere else" (Google's, shown and never kept until you pick one). → [{ id, name, detail }] */
export async function townSearch(ctx, q, session) {
  return (await maps({ op: 'search', q, kind: 'town', session, region: ctx.country || undefined, near: ctx.home || undefined })).results || [];
}
export async function forget(ctx, placeId) {
  must(await db().from('date_swipes').delete().eq('user_id', ctx.me.id).eq('place_id', placeId));
}
export async function setRange(ctx, minutes) {
  must(await db().from('couples').update({ date_range_minutes: minutes }).eq('id', ctx.coupleId));
}

/* A date on the week, for both of you (the other is asked: "Ben's up for Dundas Peak on Saturday · Are you in?",
   0045) or just you. The place is its id only; `named` says the title is our catalogue's name for it, not a kind. */
export async function plan(ctx, { title, date, start, minutes, who, placeId, kind, named }) {
  const hh = (m) => String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
  const row = { couple_id: ctx.coupleId, created_by: ctx.me.id, title: String(title || 'A date').slice(0, 120), on_date: date,
    start_time: start != null ? hh(start) : null, minutes: minutes || null, who: who || 'both', source: 'courted',
    ref: { place: { id: placeId }, kind: kind || null, named: !!named }, status: 'planned' };
  const out = must(await db().from('plans').insert(row).select('id, title, on_date, start_time, minutes, who, created_by, agreed_by, ref').single());
  // a date you've asked about: the app may offer notifications, to hear the answer (src/shell/push-ask.js)
  if (row.who !== 'me') try { window.dispatchEvent(new CustomEvent('dv-moment', { detail: { kind: 'plan' } })); } catch (e) {}
  return out;
}
/* Take a date off the week (a rained-off hike swapped for something inside): dropped, as Home's × does. */
export async function dropPlan(ctx, id) {
  return must(await db().from('plans').update({ status: 'dropped' }).eq('id', id).eq('couple_id', ctx.coupleId));
}
/* Courted's dates in the next days (This week shows them as planned). */
export async function planned(ctx, from, to) {
  const rows = must(await db().from('plans').select('id, title, on_date, start_time, minutes, who, created_by, agreed_by, ref').eq('couple_id', ctx.coupleId)
    .eq('source', 'courted').eq('status', 'planned').gte('on_date', from).lt('on_date', to).order('on_date')) || [];
  return rows.filter((p) => p.on_date >= from && p.on_date < to);
}
/* Dates that have been, from the last week, that you haven't said how they went (0048). → plans */
export async function toRate(ctx, today) {
  const d = new Date(today + 'T12:00:00'); d.setDate(d.getDate() - 7);
  const from = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  const plans = must(await db().from('plans').select('id, title, on_date, start_time, minutes, who, created_by, agreed_by, ref').eq('couple_id', ctx.coupleId)
    .eq('source', 'courted').in('status', ['planned', 'done']).gte('on_date', from).lt('on_date', today).order('on_date', { ascending: false })) || [];
  const mine = plans.filter((p) => p.on_date >= from && p.on_date < today && (p.who !== 'me' || p.created_by === ctx.me.id));
  if (!mine.length) return [];
  const seen = must(await db().from('date_visits').select('plan_id').eq('user_id', ctx.me.id).in('plan_id', mine.map((p) => p.id))) || [];
  const done = new Set(seen.map((v) => v.plan_id));
  return mine.filter((p) => !done.has(p.id) && p.ref && p.ref.place && p.ref.place.id);
}
/* How it went: 'loved' (a favourite) or 'never' (wouldn't go again, for now). Your verdict on the place too. */
export async function visit(planId, verdict) {
  return must(await db().rpc('date_visit', { p_plan: planId, p_verdict: verdict }));
}

/* Your answer to your partner's date: 'in' (they hear "It's a date") or 'out' (it comes off the week; they see it
   on Home, nobody's told). */
export async function respond(id, answer) {
  return must(await db().rpc('plan_respond', { p_plan: id, p_answer: answer }));
}

/* ---- Perfect days (0073): the household's saved days, evenings and weekends, and what's in them. A thing is our
   words: a place is its Google id with our catalogue's name or the kind as its label, never Google's name. ---- */
const PD = 'id, title, town_id, days, status, planned_on, created_by, created_at, updated_at';
const PDI = 'id, day_id, day, part, pos, kind, ref, label, place_kind, added_by, created_at';
export async function perfectDays(ctx) {
  const [d, i] = await Promise.all([
    db().from('perfect_days').select(PD).eq('couple_id', ctx.coupleId).eq('status', 'active').order('updated_at', { ascending: false }).then(must),
    db().from('perfect_day_items').select(PDI).eq('couple_id', ctx.coupleId).is('removed_at', null).then(must),
  ]);
  const by = {};
  (i || []).forEach((x) => { (by[x.day_id] = by[x.day_id] || []).push(x); });
  return (d || []).map((p) => ({ ...p, items: by[p.id] || [] }));
}
export async function newPerfectDay(ctx, { title, townId, days }) {
  const row = { couple_id: ctx.coupleId, title: String(title || '').trim().slice(0, 80) || 'A perfect day', town_id: townId || null, days: days === 3 ? 3 : 1 };
  return { ...must(await db().from('perfect_days').insert(row).select(PD).single()), items: [] };
}
/* A new name, or gone (status 'gone': off the list, its things kept; Undo brings it back). */
export async function updatePerfectDay(id, patch) {
  const ok = {};
  if (patch.title != null) ok.title = String(patch.title).trim().slice(0, 80) || 'A perfect day';
  if (patch.status) ok.status = patch.status;
  return must(await db().from('perfect_days').update(ok).eq('id', id).select(PD).single());
}
export async function addPerfectItem(ctx, x) {
  const row = { couple_id: ctx.coupleId, day_id: x.day_id, day: x.day || 0, part: x.part, pos: x.pos || 0, kind: x.kind,
    ref: x.ref ? String(x.ref).slice(0, 400) : null, label: String(x.label || '').trim().slice(0, 120) || 'Something', place_kind: x.place_kind || null };
  return must(await db().from('perfect_day_items').insert(row).select(PDI).single());
}
export async function movePerfectItem(id, { day, part, pos }) {
  return must(await db().from('perfect_day_items').update({ day, part, pos }).eq('id', id).select(PDI).single());
}
/* Taking a thing off marks it (0076), so your partner's open page hears it: realtime can't filter a delete by home. */
export async function removePerfectItem(id) {
  must(await db().from('perfect_day_items').update({ removed_at: new Date().toISOString() }).eq('id', id));
}
/* Everything on the household's week from `from` to `to` (either of you), for when a perfect day fits. */
export async function plansBetween(ctx, from, to) {
  return must(await db().from('plans').select('on_date, start_time, all_day, who, created_by').eq('couple_id', ctx.coupleId).eq('status', 'planned')
    .gte('on_date', from).lte('on_date', to)) || [];
}
/* Put it on the week: perfect_day_planned's one note first (your partner hears "Ben planned Saturday: A Toronto
   evening: …", through the evening's push), then a plan for each thing — for you both, your partner already in (you
   made it together), `ref.evening` so plans_activity doesn't ask about each one. `rows` are made by the app (a dinner
   from your recipes goes on Catered's plan instead, through Catered's own write). */
export async function planPerfectDay(ctx, dayId, date, rows) {
  must(await db().rpc('perfect_day_planned', { p_day: dayId, p_date: date }));
  const now = new Date().toISOString();
  const full = (rows || []).map((r) => ({ couple_id: ctx.coupleId, created_by: ctx.me.id, who: 'both', status: 'planned',
    agreed_by: ctx.partner ? ctx.partner.id : null, agreed_at: ctx.partner ? now : null, ...r, title: String(r.title || 'Something').slice(0, 120),
    ref: { ...(r.ref || {}), evening: true, perfect: dayId } }));
  if (full.length) must(await db().from('plans').insert(full));
}

/* Partner answers as they happen. */
let channel = null;
export function subscribe(ctx, fn, onPerfect) {
  unsubscribe();
  let ch = db().channel('courted:' + ctx.coupleId)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'date_swipes', filter: 'couple_id=eq.' + ctx.coupleId }, fn);
  // what your partner adds to a perfect day (0075)
  if (onPerfect) {
    ch = ch.on('postgres_changes', { event: '*', schema: 'public', table: 'perfect_days', filter: 'couple_id=eq.' + ctx.coupleId }, onPerfect)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'perfect_day_items', filter: 'couple_id=eq.' + ctx.coupleId }, onPerfect);
  }
  channel = ch.subscribe();
}
export function unsubscribe() { if (channel) { try { db().removeChannel(channel); } catch (e) {} channel = null; } }

/* ---- the deck and the cards (courted-places) ---- */
async function call(body) {
  const s = await currentSession();
  if (!s) throw new Error('signin');
  const r = await fetch(API_URL + '/functions/v1/courted-places', {
    method: 'POST', headers: { 'content-type': 'application/json', apikey: PUBLISHABLE_KEY, authorization: 'Bearer ' + s.access_token },
    body: JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) { const e = new Error(j.error || 'http_' + r.status); e.status = r.status; throw e; }
  return j;
}
const DECK = {};
/* The ids near home (or around a town), by kind; kept for the session per range and town. */
export async function deck(range, kinds, town) {
  const k = (town ? 'town:' + town : range) + ':' + (kinds || []).join(',');
  if (!DECK[k]) DECK[k] = call({ op: 'deck', kinds, ...(town ? { town } : {}) }).catch((e) => { delete DECK[k]; throw e; });
  return DECK[k];
}
export const forgetDeck = () => { Object.keys(DECK).forEach((k) => delete DECK[k]); };
const CARDS = {}, WAIT = {};
/* Cards for these ids (ten a call), from memory when we have them; in a town, without the drive from home.
   → { id: card } */
export async function cards(ids, town) {
  const want = [...new Set(ids)].filter((id) => !CARDS[id] && !WAIT[id]);
  for (let i = 0; i < want.length; i += 10) {
    const part = want.slice(i, i + 10);
    const p = call({ op: 'cards', ids: part, ...(town ? { town } : {}) }).then((j) => { (j.cards || []).forEach((c) => { CARDS[c.id] = c; }); part.forEach((id) => { if (!CARDS[id]) CARDS[id] = { id, missing: true }; }); })
      .finally(() => part.forEach((id) => delete WAIT[id]));
    part.forEach((id) => { WAIT[id] = p; });
  }
  await Promise.all(ids.map((id) => WAIT[id]).filter(Boolean)).catch(() => {});
  const out = {};
  ids.forEach((id) => { if (CARDS[id]) out[id] = CARDS[id]; });
  return out;
}
export const cardNow = (id) => CARDS[id] || null;
/* Somewhere of your own (Find a spot's "Somewhere else", Ben 29 Oct): Google's place search through the maps
   function, ranked near home. Shown, never kept. → [{ id, name, detail }] */
export async function search(ctx, q, session) {
  const s = await currentSession();
  if (!s) throw new Error('signin');
  const r = await fetch(API_URL + '/functions/v1/maps', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: 'Bearer ' + s.access_token, apikey: PUBLISHABLE_KEY },
    body: JSON.stringify({ op: 'search', q, kind: 'any', session, region: ctx.country || undefined, near: ctx.home || undefined }),
  });
  const j = await r.json().catch(() => ({ ok: false, error: 'bad_reply' }));
  if (!j.ok) { const e = new Error(j.error || 'maps'); e.status = r.status; throw e; }
  return j.results || [];
}
/* The place's own website (Google's, for a place our catalogue has no link for), asked only on its page. → url | null */
const SITES = {};
export function site(id) {
  if (!id) return Promise.resolve(null);
  if (!(id in SITES)) SITES[id] = call({ op: 'site', id }).then((j) => j.url || null).catch(() => { delete SITES[id]; return null; });
  return SITES[id];
}
/* Google's rating, how many reviews, its one line about the place and the website (the dearer tier: asked only
   for the card on top and a place's page; a search brings them for its results, without the website). Kept for
   the session, never stored. → { rating, count, summary, site } | null (site undefined: not asked yet) */
const ABOUT = {};
export const aboutNow = (id) => (ABOUT[id] && ABOUT[id].v) || null;
export function about(id) {
  if (!id) return Promise.resolve(null);
  const a = ABOUT[id];
  if (a) return a.p || Promise.resolve(a.v);
  const p = call({ op: 'about', id }).then((j) => { const v = { rating: j.rating, count: j.count, summary: j.summary, site: j.site || null }; ABOUT[id] = { v }; return v; })
    .catch(() => { delete ABOUT[id]; return null; });
  ABOUT[id] = { v: null, p };
  return p;
}
/* Search near home or in a town: Google's places, best rated first among those with 200 reviews or more. Each
   comes back as a card (kept for the session like the deck's) with its rating and line. → [card] */
const FOUND = {};
export async function searchPlaces(q, town) {
  const k = (town || 'home') + ':' + q.trim().toLowerCase();
  if (!FOUND[k]) FOUND[k] = call({ op: 'search', q, ...(town ? { town } : {}) }).then((j) => (j.results || []).map((c) => {
    if (!CARDS[c.id]) CARDS[c.id] = { id: c.id, name: c.name, type: c.type, typeLabel: c.typeLabel, address: c.address, maps: c.maps, photo: c.photo, minutes: null };
    if (!ABOUT[c.id] || !ABOUT[c.id].v) ABOUT[c.id] = { v: { rating: c.rating, count: c.count, summary: c.summary, site: undefined } };
    return c;
  })).catch((e) => { delete FOUND[k]; throw e; });
  return FOUND[k];
}
const PHOTOS = {};
/* One photo, for the card being looked at. → url | null */
export function photo(name) {
  if (!name) return Promise.resolve(null);
  if (!PHOTOS[name]) PHOTOS[name] = call({ op: 'photo', name }).then((j) => j.url || null).catch(() => { delete PHOTOS[name]; return null; });
  return PHOTOS[name];
}

/* ---- the weather at home (T3): Google's daily forecast through courted-places, kept for the session for up to
   three hours (Google allows a day). → { tz, days } | null. Home, Discover and Courted share this one read. ---- */
let WXP = null, WX_AT = 0;
export function weather() {
  if (!WXP || Date.now() - WX_AT > 3 * 3600 * 1000) {
    WX_AT = Date.now();
    WXP = call({ op: 'weather' }).then((j) => ({ tz: j.tz || null, days: j.days || [] })).catch(() => { WXP = null; return null; });
  }
  return WXP;
}
/* The outdoor places you both like (a home of one: yours), with their kind — for the weather's nudge. */
export async function outdoorIdeas(ctx) {
  const rows = await swipes(ctx).catch(() => null);
  if (!rows) return [];
  const H = await import('./helpers.js');
  // near home only: a hike you liked on a weekend away isn't Saturday's (Go out anywhere)
  return H.ideasOf(H.rowsIn(rows, null), ctx.me.id, ctx.partner && ctx.partner.id).filter((i) => i.kind === 'outdoors');
}

/* ---- our words for the places near home (the catalogue, public): the areas within reach, then their places ---- */
const CAT_H = { apikey: CATALOGUE_KEY, authorization: 'Bearer ' + CATALOGUE_KEY };
const FIELDS = 'place_id,city,kind,name,what,line,tags,outing,minutes,daylight,booking_note,booking_url,booking_until,trail_km,trail_shape,effort,open_months,website';
let AREAS = null;
export function areas() {
  if (!AREAS) AREAS = fetch(CATALOGUE_URL + '/rest/v1/date_areas?select=key,name,country_code,lat,lng,radius_km', { headers: CAT_H })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http_' + r.status)))).catch(() => { AREAS = null; return []; });
  return AREAS;
}
const CATS = {};
export async function catalogue(ctx, town) {
  if (!ctx || !(town || ctx.home)) return [];
  // around a town: the areas whose centre is within reach of it (Toronto's, for Toronto); near home, within your range
  const near = town ? areasNear(await areas(), { lat: Number(town.lat), lng: Number(town.lng) }, null, Number(town.km) || 12) : areasNear(await areas(), ctx.home, ctx.range);
  if (!near.length) return [];
  const k = near.join(',');
  if (!CATS[k]) {
    CATS[k] = fetch(CATALOGUE_URL + '/rest/v1/date_places?select=' + FIELDS + '&status=eq.published&city=in.(' + near.map(encodeURIComponent).join(',') + ')', { headers: CAT_H })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http_' + r.status)))).catch(() => { delete CATS[k]; return []; });
  }
  return CATS[k];
}

/* What to call places on the week: our name when the catalogue has the place, else Google's, fetched now and kept
   for the session (only the id is stored). → { id: name } */
export async function placeNames(ctx, ids) {
  const want = [...new Set((ids || []).filter(Boolean))];
  if (!want.length) return {};
  const out = {};
  (await catalogue(ctx).catch(() => [])).forEach((c) => { if (want.includes(c.place_id)) out[c.place_id] = c.name; });
  const rest = want.filter((id) => !out[id]);
  if (rest.length) Object.values(await cards(rest).catch(() => ({}))).forEach((c) => { if (c && c.name) out[c.id] = c.name; });
  return out;
}

/* ---- In theatres: what's in cinemas in your country (TMDB, through curated-tmdb), and which of those you two
   said yes to in Curated ---- */
const NOWP = {};
export function nowPlaying(country) {
  const region = ['CA', 'GB', 'US'].includes(country) ? country : 'CA';
  if (!NOWP[region]) {
    NOWP[region] = fetch(API_URL + '/functions/v1/curated-tmdb', { method: 'POST', headers: { 'content-type': 'application/json', apikey: PUBLISHABLE_KEY },
      body: JSON.stringify({ action: 'now_playing', region }) })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j) => (j.items || []).map((x) => x.title).filter(Boolean).map((t) => ({ id: t.tmdb_id, title: t.title, poster: t.poster_path || null, runtime: t.runtime_minutes || null, genres: t.genres || [], popularity: Number(t.popularity) || 0 })))
      .catch(() => { delete NOWP[region]; return []; });
  }
  return NOWP[region];
}
/* Curated's answers on those films (partners read each other's swipes). */
export async function filmSwipes(ids) {
  if (!ids.length) return [];
  return must(await db().from('swipes').select('tmdb_id, user_id, decision').eq('media_type', 'movie').in('tmdb_id', ids)) || [];
}
